import { useState } from 'react'
import { Button, Checkbox, Input, message, Segmented, Select, Space, Table, Tag, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { copyText, regexAI, regexTest } from '@/api/logText'
import type { RegexAIResult, RegexMatchItem, RegexTestResult } from '@/types'

const { TextArea } = Input
const { Text } = Typography
const MONO = { fontFamily: "'JetBrains Mono', 'Consolas', monospace", fontSize: 12 }

const FLAG_OPTIONS = ['IGNORECASE', 'MULTILINE', 'DOTALL', 'ASCII'].map((v) => ({ label: v, value: v }))

/** 正则生成与解释页签：AI 生成/解释 + 本地试跑验证（防幻觉） */
export default function RegexTab() {
  // AI 生成/解释
  const [aiMode, setAiMode] = useState<'generate' | 'explain'>('generate')
  const [aiInput, setAiInput] = useState('')
  const [aiLoading, setAiLoading] = useState(false)
  const [aiResult, setAiResult] = useState<RegexAIResult | null>(null)

  // 本地试跑
  const [pattern, setPattern] = useState('')
  const [flags, setFlags] = useState<string[]>([])
  const [mode, setMode] = useState<'findall' | 'search' | 'match' | 'fullmatch'>('findall')
  const [text, setText] = useState('')
  const [testing, setTesting] = useState(false)
  const [testResult, setTestResult] = useState<RegexTestResult | null>(null)

  /** AI 生成/解释（generate 成功后自动填入试跑） */
  const handleRunAI = async () => {
    if (!aiInput.trim()) {
      message.warning(aiMode === 'generate' ? '请描述正则需求' : '请输入要解释的正则')
      return
    }
    setAiLoading(true)
    try {
      const res = await regexAI({ action: aiMode, content: aiInput })
      setAiResult(res)
      if (res.parsed && res.regex) setPattern(res.regex)
    } finally {
      setAiLoading(false)
    }
  }

  /** 本地试跑（AI 结果必须经此验证） */
  const handleTest = async () => {
    if (!pattern.trim() || !text) {
      message.warning('请填写正则与测试文本')
      return
    }
    setTesting(true)
    try {
      setTestResult(await regexTest({ pattern, flags, text, mode }))
    } finally {
      setTesting(false)
    }
  }

  const matchColumns: ColumnsType<RegexMatchItem> = [
    { title: '匹配结果', dataIndex: 'match', ellipsis: true, render: (v: string) => <span style={MONO}>{v}</span> },
    { title: '分组', dataIndex: 'groups', width: 200, ellipsis: true, render: (g: (string | null)[]) => <span style={MONO}>{g.length ? g.map((x) => x ?? 'null').join(' | ') : '-'}</span> },
    { title: '命名分组', dataIndex: 'named', width: 180, ellipsis: true, render: (n: Record<string, string>) => (n && Object.keys(n).length ? <span style={MONO}>{Object.entries(n).map(([k, v]) => `${k}=${v}`).join(', ')}</span> : '-') },
    { title: '位置', width: 110, render: (_: unknown, r: RegexMatchItem) => <span style={MONO}>{r.start}-{r.end}</span> },
    {
      title: '操作', width: 70, render: (_: unknown, r: RegexMatchItem) => (
        <Button type="link" size="small" style={{ padding: 0 }} onClick={() => copyText(r.match)}>复制</Button>
      ),
    },
  ]

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 14, padding: '0 22px 12px', overflowY: 'auto' }}>
      {/* ===== AI 生成 / 解释 ===== */}
      <div style={{ flexShrink: 0 }}>
        <Text strong style={{ display: 'block', marginBottom: 8 }}>AI 生成与解释（复用 AI 配置中的模型）</Text>
        <Space direction="vertical" style={{ width: '100%' }} size={8}>
          <Space wrap>
            <Segmented
              value={aiMode}
              onChange={(v) => { setAiMode(v as 'generate' | 'explain'); setAiResult(null) }}
              options={[{ value: 'generate', label: '自然语言 → 正则' }, { value: 'explain', label: '正则 → 解释' }]}
            />
            <Button loading={aiLoading} onClick={handleRunAI}>{aiMode === 'generate' ? '生成' : '解释'}</Button>
          </Space>
          <TextArea
            rows={2}
            placeholder={aiMode === 'generate' ? '例：匹配 11 位手机号，1 开头第二位是 3-9' : '例：^\d{4}-\d{2}-\d{2}$'}
            value={aiInput}
            onChange={(e) => setAiInput(e.target.value)}
          />
        </Space>
        {aiResult && !aiResult.parsed && (
          <div style={{ marginTop: 8 }}>
            <Tag color="warning">AI 返回格式未解析，原文：</Tag>
            <pre style={{ ...MONO, whiteSpace: 'pre-wrap', margin: '6px 0 0' }}>{aiResult.raw}</pre>
          </div>
        )}
        {aiResult?.parsed && (
          <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
            {aiMode === 'generate' ? (
              <>
                <Space wrap>
                  <Tag color="purple" style={MONO}>{aiResult.regex}</Tag>
                  <Button type="link" size="small" style={{ padding: 0 }} onClick={() => copyText(aiResult.regex ?? '')}>复制表达式</Button>
                  {(aiResult.python_flags ?? []).map((f) => <Tag key={f}>{f}</Tag>)}
                </Space>
                <Text>{aiResult.explanation}</Text>
                {aiResult.notes && <Text type="secondary">注意：{aiResult.notes}</Text>}
              </>
            ) : (
              <>
                <Text>{aiResult.explanation}</Text>
                {(aiResult.components ?? []).map((c) => (
                  <Space key={c.part} wrap>
                    <Tag color="cyan" style={MONO}>{c.part}</Tag>
                    <Text type="secondary">{c.meaning}</Text>
                  </Space>
                ))}
                <Space wrap>
                  {(aiResult.examples ?? []).map((ex, i) => (
                    <Tag key={i} color={ex.match ? 'green' : 'red'} style={MONO}>{ex.text} → {ex.match ? '命中' : '不命中'}</Tag>
                  ))}
                </Space>
              </>
            )}
          </div>
        )}
      </div>

      {/* ===== 本地试跑（防幻觉验证） ===== */}
      <div style={{ flexShrink: 0 }}>
        <Text strong style={{ display: 'block', marginBottom: 8 }}>在线试跑（AI 结果必须本地验证）</Text>
        <Space wrap style={{ marginBottom: 8 }}>
          <Input
            placeholder="正则表达式，如 \d{4}-\d{2}-\d{2}"
            value={pattern}
            onChange={(e) => setPattern(e.target.value)}
            style={{ width: 300, ...MONO }}
          />
          <Checkbox.Group options={FLAG_OPTIONS} value={flags} onChange={(v) => setFlags(v as string[])} />
          <Select
            value={mode}
            onChange={setMode}
            style={{ width: 130 }}
            options={[
              { value: 'findall', label: 'findall 全部' },
              { value: 'search', label: 'search 首个' },
              { value: 'match', label: 'match 起始' },
              { value: 'fullmatch', label: 'fullmatch 全匹配' },
            ]}
          />
          <Button type="primary" style={{ background: 'var(--au-primary)', border: 'none' }} loading={testing} onClick={handleTest}>
            试跑
          </Button>
        </Space>
        <TextArea rows={3} placeholder="粘贴测试文本" value={text} onChange={(e) => setText(e.target.value)} style={MONO} />
        {testResult && (
          <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {!testResult.valid ? (
              <Tag color="error">{testResult.error}</Tag>
            ) : (
              <>
                <Space wrap>
                  <Tag color={testResult.match_count ? 'green' : 'orange'}>命中 {testResult.match_count} 处</Tag>
                  <Tag>{testResult.mode}</Tag>
                  {(testResult.flags ?? []).map((f) => <Tag key={f}>{f}</Tag>)}
                </Space>
                <div style={{ ...MONO, whiteSpace: 'pre-wrap', background: 'var(--au-surface, #fafafa)', padding: 10, borderRadius: 8, maxHeight: 200, overflowY: 'auto' }}>
                  {(testResult.segments ?? []).map((s, i) =>
                    s.hit ? <span key={i} style={{ background: '#ffe58f', borderRadius: 2 }}>{s.text}</span> : <span key={i}>{s.text}</span>,
                  )}
                </div>
                {(testResult.matches ?? []).length > 0 && (
                  <Table size="small" columns={matchColumns} dataSource={testResult.matches} rowKey={(_, i) => String(i)} pagination={{ pageSize: 5 }} />
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
